import { ElementIdSchema, RoleIdSchema } from '@dpem/ontology';
import * as z from 'zod';

import {
  AutomationLevelSchema,
  BindingKindSchema,
  ParameterValueTypeSchema,
} from '../common/enums.js';
import { PageQuerySchema, paginated } from '../common/pagination.js';
import {
  DescriptionSchema,
  IdentifierSchema,
  IsoDateSchema,
  IsoDateTimeSchema,
  LabelSchema,
  OrderSchema,
  ParameterKeySchema,
  QueryIntSchema,
  RevisionSchema,
  SentenceSchema,
  ShortTextSchema,
  TextFilterSchema,
  sortBy,
} from '../common/primitives.js';
import { MethodVersionSummarySchema } from './methods.js';

/**
 * planning context — the work breakdown a method turns into before a project starts.
 *
 * A method says what good work looks like; it does not say who does it on which day. A
 * plan is the second half of that sentence, and it is generated from two inputs rather
 * than one: **the method** (its phases, stages, work practices, activities, gates,
 * ceremonies, roles and the tools, assets, agents, skills and MCP servers its activities
 * are bound to) and **a plan template** (how this organisation sequences work — sprints,
 * program increments, stage gates, continuous flow). Neither alone is enough. The method
 * has no cadence and the template has no content, so a generator that read only one of
 * them would produce either an unsequenced list of activities or an empty sprint calendar.
 *
 * Three properties are worth stating up front, because they are what make a generated plan
 * usable rather than a demo:
 *
 *  - **A plan is generated, then curated.** Every task records where it came from
 *    (`origin`) and whether a human has since edited it (`curated`), so `regenerate`
 *    can replay the generator over a changed method and keep the planner's work.
 *  - **The schedule is offsets, not dates.** Tasks carry `earliestStartDay` and
 *    `durationDays` in working days from the plan's start; the plan carries `startsOn` and
 *    `workingDaysPerWeek`, and the calendar is projected from those. A generator that
 *    minted dates would own a holiday calendar and a timezone, and would be wrong in
 *    every country but one.
 *  - **Adoption feeds planning.** A plan may be generated from a base method or from a
 *    published tailored variant, and in the latter case it may also name the workshop
 *    session it came out of. That link is not decoration: the session is where the
 *    client's binding *configuration* text lives ("Jira project ACME, two boards"), and
 *    where the parameter answers were given. Neither survives into the published graph,
 *    and both are exactly what the pre-launch tool-setup and asset-configuration tasks
 *    have to say.
 */

/* ── vocabulary ─────────────────────────────────────────────────────────── */

/**
 * Plan lifecycle. Three states, because a plan has three honest conditions: being built,
 * agreed, and superseded. No `IN_REVIEW` — a plan is reviewed in the room it is presented
 * in, and a state nothing in the platform transitions out of is a state that strands rows.
 */
export const PLAN_STATES = ['DRAFT', 'BASELINED', 'ARCHIVED'] as const;
export const PlanStateSchema = z.enum(PLAN_STATES);
export type PlanState = z.infer<typeof PlanStateSchema>;

/**
 * The built-in templates. Keys are stable and kebab-cased because they appear in URLs and
 * in the `plans.template_key` column; the display name may change, the key may not.
 */
export const PLAN_TEMPLATE_KEYS = [
  'waterfall-stage-gate',
  'agile-scrum',
  'safe-art',
  'kanban-flow',
  'hybrid-stage-gate-agile',
] as const;
export const PlanTemplateKeySchema = z.enum(PLAN_TEMPLATE_KEYS);
export type PlanTemplateKey = z.infer<typeof PlanTemplateKeySchema>;

/**
 * How a template arranges delivery. The family is what a reader needs before the detail:
 * it says whether the plan will have phases or iterations, and whether "when is it done"
 * is answered by a date or by a flow rate.
 */
export const PLAN_TEMPLATE_FAMILIES = [
  'SEQUENTIAL',
  'ITERATIVE',
  'SCALED_ITERATIVE',
  'FLOW',
  'HYBRID',
] as const;
export const PlanTemplateFamilySchema = z.enum(PLAN_TEMPLATE_FAMILIES);
export type PlanTemplateFamily = z.infer<typeof PlanTemplateFamilySchema>;

/**
 * The four parts of any plan the platform generates.
 *
 * `MOBILISATION` is everything that has to be true before day one — training on the
 * method, tool setup, asset configuration, agent and MCP enablement. It exists as a
 * section rather than as a tag because it is the part a sponsor asks about first and the
 * part a generated plan usually forgets: an adoption that stands up a method without
 * standing up its tooling is an adoption that fails in week two.
 */
export const PLAN_SECTIONS = ['MOBILISATION', 'DELIVERY', 'GOVERNANCE', 'CLOSE_OUT'] as const;
export const PlanSectionSchema = z.enum(PLAN_SECTIONS);
export type PlanSection = z.infer<typeof PlanSectionSchema>;

/**
 * What a row in the breakdown is.
 *
 * `SUMMARY` and `ITERATION` are containers and carry no duration of their own — theirs is
 * the span of what they contain. `MILESTONE` is a zero-duration decision point (a gate).
 * `CEREMONY`, `TRAINING` and `SETUP` are separated from plain `TASK` because a plan is read
 * by three different people: a delivery lead filters on tasks, an enablement lead on
 * training and setup, and a scrum master on ceremonies.
 */
export const PLAN_TASK_KINDS = [
  'SUMMARY',
  'ITERATION',
  'TASK',
  'MILESTONE',
  'CEREMONY',
  'TRAINING',
  'SETUP',
] as const;
export const PlanTaskKindSchema = z.enum(PLAN_TASK_KINDS);
export type PlanTaskKind = z.infer<typeof PlanTaskKindSchema>;

/**
 * Where a task came from — the field that makes regeneration safe.
 *
 * `regenerate` deletes and rebuilds everything the generator owns, and keeps what it does
 * not: a `MANUAL` task a planner added, and any generated task they have edited (see
 * `curated`). Without this the choice would be between never regenerating and throwing a
 * planner's afternoon away.
 */
export const PLAN_TASK_ORIGINS = [
  'METHOD_ELEMENT',
  'TEMPLATE',
  'BINDING',
  'TRAINING',
  'MANUAL',
] as const;
export const PlanTaskOriginSchema = z.enum(PLAN_TASK_ORIGINS);
export type PlanTaskOrigin = z.infer<typeof PlanTaskOriginSchema>;

/**
 * Dependency semantics, in the three forms that carry their weight. No finish-to-start's
 * mirror image (`START_TO_FINISH`): it models "this cannot end until that begins", which no
 * generator here emits and no planner has ever asked a tool for.
 */
export const PLAN_DEPENDENCY_KINDS = [
  'FINISH_TO_START',
  'START_TO_START',
  'FINISH_TO_FINISH',
] as const;
export const PlanDependencyKindSchema = z.enum(PLAN_DEPENDENCY_KINDS);
export type PlanDependencyKind = z.infer<typeof PlanDependencyKindSchema>;

/**
 * Why the dependency is there. A planner deleting an edge deserves to know whether they
 * are contradicting the method (`SEQUENCE`, `GATE`), the template (`TEMPLATE`), the
 * client's tooling (`ENABLEMENT` — you cannot run the activity before its tool is set up)
 * or only themselves (`MANUAL`).
 */
export const PLAN_DEPENDENCY_ORIGINS = [
  'CONTAINMENT',
  'SEQUENCE',
  'GATE',
  'ENABLEMENT',
  'TEMPLATE',
  'MANUAL',
] as const;
export const PlanDependencyOriginSchema = z.enum(PLAN_DEPENDENCY_ORIGINS);
export type PlanDependencyOrigin = z.infer<typeof PlanDependencyOriginSchema>;

/** How often something inside a task's span happens. Display truth, not scheduling truth. */
export const PLAN_RECURRENCES = [
  'ONCE',
  'DAILY',
  'WEEKLY',
  'FORTNIGHTLY',
  'MONTHLY',
  'PER_ITERATION',
  'PER_INCREMENT',
] as const;
export const PlanRecurrenceSchema = z.enum(PLAN_RECURRENCES);
export type PlanRecurrence = z.infer<typeof PlanRecurrenceSchema>;

/**
 * Where a parameter value used by the generation came from.
 *
 * Recorded per value because the three have different authority: `CALLER` is what the
 * planner typed into the generate form, `WORKSHOP` is what the client already answered in
 * their adoption session, and `TEMPLATE_DEFAULT` is a guess the template made. A plan that
 * cannot tell them apart cannot tell a sponsor which of its numbers they agreed to.
 */
export const PLAN_PARAMETER_SOURCES = ['CALLER', 'WORKSHOP', 'TEMPLATE_DEFAULT'] as const;
export const PlanParameterSourceSchema = z.enum(PLAN_PARAMETER_SOURCES);
export type PlanParameterSource = z.infer<typeof PlanParameterSourceSchema>;

/**
 * What the generator could not do properly, in the language of the thing it could not do.
 *
 * A generated plan is a claim about a client's next six months, so it says where it was
 * guessing. Every code below is a place a human has to look: an activity with no role
 * bound has no owner, a bound tool with no configuration text will be set up by somebody
 * who has to ask what "set up Jira" means, and an unanswered parameter means a duration in
 * the plan came from a template default rather than from the client.
 */
export const PLAN_WARNING_CODES = [
  'NO_OWNER',
  'NO_DURATION_SOURCE',
  'UNANSWERED_PARAMETER',
  'UNKNOWN_PARAMETER',
  'SKIPPED_ELEMENT',
  'EMPTY_CONTAINER',
  'BINDING_WITHOUT_CONFIGURATION',
  'CAPACITY_OVERFLOW',
  'CYCLE_BROKEN',
] as const;
export const PlanWarningCodeSchema = z.enum(PLAN_WARNING_CODES);
export type PlanWarningCode = z.infer<typeof PlanWarningCodeSchema>;

/**
 * The method element kinds a template has to say something about. The eight authorable
 * kinds plus `deliverable`, which is not authored on the canvas but is planned work all the
 * same — somebody writes the document.
 */
export const PLANNABLE_ELEMENT_KINDS = [
  'phase',
  'stage',
  'workPractice',
  'activity',
  'role',
  'gate',
  'lens',
  'ceremony',
  'deliverable',
] as const;
export const PlannableElementKindSchema = z.enum(PLANNABLE_ELEMENT_KINDS);
export type PlannableElementKind = z.infer<typeof PlannableElementKindSchema>;

/**
 * What a template does with a kind of method element. This is the mapping that makes two
 * plans generated from one method look nothing like each other: in `waterfall-stage-gate` a
 * phase is a summary bar and its activities are sequenced tasks beneath it, while in
 * `agile-scrum` the phases become release goals and the same activities become backlog
 * items drawn into iterations.
 */
export const PLAN_ELEMENT_TREATMENTS = [
  'SUMMARY',
  'ITERATION_CONTAINER',
  'TASK',
  'BACKLOG_ITEM',
  'FLOW_ITEM',
  'MILESTONE',
  'CEREMONY',
  'DELIVERABLE_TASK',
  'TRAINING_SUBJECT',
  'OWNER_ONLY',
  'IGNORED',
] as const;
export const PlanElementTreatmentSchema = z.enum(PLAN_ELEMENT_TREATMENTS);
export type PlanElementTreatment = z.infer<typeof PlanElementTreatmentSchema>;

export const PLAN_SORT_FIELDS = ['name', 'updatedAt', 'generatedAt', 'startsOn'] as const;

/* ── scalars ────────────────────────────────────────────────────────────── */

/**
 * A duration in working days. Fractional because half-day ceremonies are the norm and
 * rounding a daily scrum up to a day would add a week of phantom work to a sprint plan.
 * Capped at ten years, which is not a plan.
 */
export const DurationDaysSchema = z.number().min(0).max(3_650);

/** Offset in working days from the plan's `startsOn`. */
export const DayOffsetSchema = z.int().min(0).max(10_000);

/**
 * Multiplier applied to a default duration. Used for automation: an `autonomous-with-approval`
 * activity is not the same amount of human work as a manual one, and a plan that pretends
 * otherwise hides the entire benefit of an agentic method.
 */
export const DurationFactorSchema = z.number().min(0.05).max(4);

/** A scaffold task's handle inside its template. Referenced by `dependsOn`. */
export const TemplateTaskKeySchema = z
  .string()
  .min(2)
  .max(48)
  .regex(/^[a-z][a-z0-9-]*$/u, { error: 'must be lower-case letters, digits and hyphens' });

/** `1`, `1.2`, `1.2.3` — the WBS code, assigned by position and rewritten on every move. */
export const WbsCodeSchema = z
  .string()
  .min(1)
  .max(48)
  .regex(/^[0-9]+(?:\.[0-9]+)*$/u, { error: 'must be dot-separated positive integers' });

/* ── templates ──────────────────────────────────────────────────────────── */

/**
 * A question the template needs answered before it can generate.
 *
 * Deliberately the same vocabulary as a method parameter (`ParameterValueTypeSchema`),
 * because they are the same kind of thing answered by different people at different times:
 * the client answers `sprint.length` in their adoption workshop, and if they have, the
 * generator uses their answer instead of asking again. One vocabulary is what makes that
 * hand-off possible.
 */
export const PlanTemplateParameterSchema = z.strictObject({
  key: ParameterKeySchema,
  label: LabelSchema,
  description: SentenceSchema,
  valueType: ParameterValueTypeSchema,
  /** Non-empty only for `ENUM`. */
  allowedValues: z.array(ShortTextSchema).max(32),
  /** What the generator uses when nobody answered. `null` means it cannot generate without one. */
  defaultValue: ShortTextSchema.nullable(),
  required: z.boolean(),
});
export type PlanTemplateParameter = z.infer<typeof PlanTemplateParameterSchema>;

/**
 * A recurring event the template adds to the calendar.
 *
 * The method may declare its own ceremonies, and those are planned from the graph. These
 * are the template's: a sprint review belongs to Scrum, not to DPSE, and a method that says
 * nothing about cadence still gets one when it is delivered in sprints.
 */
export const PlanTemplateCeremonySchema = z.strictObject({
  key: TemplateTaskKeySchema,
  name: LabelSchema,
  description: SentenceSchema,
  recurrence: PlanRecurrenceSchema,
  /** One occurrence, in working days. `0.02` is a ten-minute stand-up. */
  durationDays: DurationDaysSchema,
  section: PlanSectionSchema,
  /** Free text, not a `RoleId`: the template does not know this method's role ids. */
  ownerHint: ShortTextSchema.nullable(),
});
export type PlanTemplateCeremony = z.infer<typeof PlanTemplateCeremonySchema>;

/** One row of the template's "what it does with your method" table. */
export const PlanTemplateTreatmentSchema = z.strictObject({
  kind: PlannableElementKindSchema,
  treatment: PlanElementTreatmentSchema,
  note: SentenceSchema,
});
export type PlanTemplateTreatment = z.infer<typeof PlanTemplateTreatmentSchema>;

/**
 * A task the template contributes regardless of the method — the mobilisation work, the
 * governance rhythm, the close-out.
 *
 * `dependsOn` names sibling scaffold keys rather than ids, because the ids do not exist
 * until the plan is generated. The generator resolves them, and a key that names nothing is
 * a bug in the template rather than in the plan, so it is asserted in the template tests.
 */
export const PlanTemplateScaffoldTaskSchema = z.strictObject({
  key: TemplateTaskKeySchema,
  name: LabelSchema,
  description: SentenceSchema,
  section: PlanSectionSchema,
  kind: PlanTaskKindSchema,
  durationDays: DurationDaysSchema,
  /** When set, the duration comes from this parameter's answer and `durationDays` is the fallback. */
  durationParameterKey: ParameterKeySchema.nullable(),
  ownerHint: ShortTextSchema.nullable(),
  dependsOn: z.array(TemplateTaskKeySchema).max(16),
  /** Scaffold tasks nest one level: a mobilisation summary with its own children. */
  parentKey: TemplateTaskKeySchema.nullable(),
});
export type PlanTemplateScaffoldTask = z.infer<typeof PlanTemplateScaffoldTaskSchema>;

/**
 * How long the generator assumes things take when the method does not say.
 *
 * The method deliberately holds no durations — an activity takes as long as it takes at the
 * client — so the numbers have to come from somewhere, and a template is the honest place
 * for them: "an activity is two days" is a statement about how this organisation works, not
 * about the method. Every one of them is overridable per task afterwards, and any task that
 * used a default rather than an answer is reported as `NO_DURATION_SOURCE`.
 */
export const PlanTemplateDurationsSchema = z.strictObject({
  activityDays: DurationDaysSchema,
  deliverableDays: DurationDaysSchema,
  gateReviewDays: DurationDaysSchema,
  trainingDays: DurationDaysSchema,
  toolSetupDays: DurationDaysSchema,
  assetConfigurationDays: DurationDaysSchema,
  agentEnablementDays: DurationDaysSchema,
  /** Applied to an activity's duration according to its automation level. */
  automationFactors: z.record(AutomationLevelSchema, DurationFactorSchema),
});
export type PlanTemplateDurations = z.infer<typeof PlanTemplateDurationsSchema>;

/** Iteration geometry. All null for a sequential or flow template. */
export const PlanTemplateCadenceSchema = z.strictObject({
  timeboxed: z.boolean(),
  /** Length of one iteration in working days; `null` when the template has no iterations. */
  iterationLengthDays: DurationDaysSchema.nullable(),
  /** Iterations per increment (a SAFe PI); `null` when there is no increment above the iteration. */
  iterationsPerIncrement: z.int().min(1).max(24).nullable(),
  /** Working days per iteration a team can commit — capacity, before which nothing overflows. */
  iterationCapacityDays: DurationDaysSchema.nullable(),
});
export type PlanTemplateCadence = z.infer<typeof PlanTemplateCadenceSchema>;

/**
 * A plan template, whole.
 *
 * Served read-only: templates are code, not rows. They are versioned with the platform
 * because a template is a generator rule set — changing one changes what "generate" means,
 * and that is a deployment, not a form submission. The descriptor is on the wire in full so
 * the web surface can explain a template before anyone commits to it, and so a generated
 * plan can be read next to the rules that produced it.
 */
export const PlanTemplateSchema = z.strictObject({
  key: PlanTemplateKeySchema,
  name: LabelSchema,
  family: PlanTemplateFamilySchema,
  /** One line, for the card. */
  headline: ShortTextSchema,
  description: DescriptionSchema,
  /**
   * When to reach for this template, and — in the last item, by convention — when not to.
   *
   * A list rather than a paragraph because that is how the choice is actually made: a planner
   * scans five cards for the line that describes their situation, and prose hides it. The
   * "not for" item is the one that earns its place; a template that only says what it is good
   * for gets chosen for everything.
   */
  whenToUse: z.array(SentenceSchema).min(1).max(8),
  sections: z.array(PlanSectionSchema).min(1).max(4),
  cadence: PlanTemplateCadenceSchema,
  durations: PlanTemplateDurationsSchema,
  parameters: z.array(PlanTemplateParameterSchema).max(32),
  ceremonies: z.array(PlanTemplateCeremonySchema).max(32),
  treatments: z.array(PlanTemplateTreatmentSchema).max(16),
  scaffold: z.array(PlanTemplateScaffoldTaskSchema).max(64),
});
export type PlanTemplate = z.infer<typeof PlanTemplateSchema>;

export const ListPlanTemplatesRequestSchema = z.strictObject({
  family: PlanTemplateFamilySchema.optional(),
});
export type ListPlanTemplatesRequest = z.infer<typeof ListPlanTemplatesRequestSchema>;
export const ListPlanTemplatesResponseSchema = z.strictObject({
  templates: z.array(PlanTemplateSchema),
});
export type ListPlanTemplatesResponse = z.infer<typeof ListPlanTemplatesResponseSchema>;

export const GetPlanTemplateRequestSchema = z.strictObject({
  templateKey: PlanTemplateKeySchema,
});
export type GetPlanTemplateRequest = z.infer<typeof GetPlanTemplateRequestSchema>;
export const GetPlanTemplateResponseSchema = z.strictObject({ template: PlanTemplateSchema });
export type GetPlanTemplateResponse = z.infer<typeof GetPlanTemplateResponseSchema>;

/* ── the plan ───────────────────────────────────────────────────────────── */

/**
 * One row of the breakdown.
 *
 * Flat, with `parentTaskId` and `order`, rather than nested: the tree is rebuilt in one pass
 * on either side, a flat list patches one row without rewriting a subtree, and a dependency
 * can point anywhere without the wire shape having to nest one task inside another.
 */
export const PlanTaskSchema = z.strictObject({
  id: IdentifierSchema,
  parentTaskId: IdentifierSchema.nullable(),
  wbsCode: WbsCodeSchema,
  order: OrderSchema,
  name: LabelSchema,
  description: DescriptionSchema.nullable(),
  kind: PlanTaskKindSchema,
  section: PlanSectionSchema,
  origin: PlanTaskOriginSchema,
  /** The method element this task plans, when there is one. */
  sourceElementId: ElementIdSchema.nullable(),
  /** Set on `SETUP` tasks: which catalogue the thing being stood up came from. */
  bindingKind: BindingKindSchema.nullable(),
  /** What the client's binding override said. The reason a setup task is actionable. */
  configuration: DescriptionSchema.nullable(),
  /** The method role accountable for the work, from the activity's `performedBy`. */
  ownerRoleId: RoleIdSchema.nullable(),
  /** A person or team named during curation. The method knows roles; only a client knows names. */
  ownerName: ShortTextSchema.nullable(),
  /** 1-based iteration this task was drawn into, for iterative templates. */
  iteration: z.int().min(1).nullable(),
  /** 1-based increment (PI) this task's iteration belongs to. */
  increment: z.int().min(1).nullable(),
  /** Zero for containers and milestones — theirs is the span of what they hold. */
  durationDays: DurationDaysSchema,
  earliestStartDay: DayOffsetSchema,
  /**
   * Siblings sharing a group run in parallel: the method sequenced neither before the
   * other. Derivable from the dependency set, and carried anyway — "what can we do at the
   * same time" is the first question asked of a plan, and it should not need a graph walk.
   */
  parallelGroup: ShortTextSchema.nullable(),
  /** How often the work inside the span happens. `null` for anything that happens once. */
  recurrence: PlanRecurrenceSchema.nullable(),
  /** True once a human has edited this row. Regeneration preserves it. */
  curated: z.boolean(),
});
export type PlanTask = z.infer<typeof PlanTaskSchema>;

export const PlanTaskDependencySchema = z.strictObject({
  id: IdentifierSchema,
  fromTaskId: IdentifierSchema,
  toTaskId: IdentifierSchema,
  kind: PlanDependencyKindSchema,
  origin: PlanDependencyOriginSchema,
  /** Working days of lag; negative is a lead. */
  lagDays: z.int().min(-365).max(365),
});
export type PlanTaskDependency = z.infer<typeof PlanTaskDependencySchema>;

/** A parameter value the generation used, and who supplied it. */
export const PlanParameterValueSchema = z.strictObject({
  key: ParameterKeySchema,
  label: LabelSchema,
  valueType: ParameterValueTypeSchema,
  value: ShortTextSchema.nullable(),
  source: PlanParameterSourceSchema,
});
export type PlanParameterValue = z.infer<typeof PlanParameterValueSchema>;

export const PlanWarningSchema = z.strictObject({
  code: PlanWarningCodeSchema,
  /** The plan task or method element the warning is about, when it is about one. */
  subjectId: ShortTextSchema.nullable(),
  detail: SentenceSchema,
});
export type PlanWarning = z.infer<typeof PlanWarningSchema>;

/**
 * What the generation did and what it could not do.
 *
 * Held with the plan rather than returned once, because the questions it answers
 * ("why has this task no owner?", "which of these numbers did the client actually agree
 * to?") are asked in the review meeting a fortnight later, by somebody who did not press
 * the button.
 */
export const PlanGenerationReportSchema = z.strictObject({
  generatedAt: IsoDateTimeSchema,
  templateKey: PlanTemplateKeySchema,
  sourceMethodVersionId: IdentifierSchema,
  taskCount: z.int().min(0),
  milestoneCount: z.int().min(0),
  trainingTaskCount: z.int().min(0),
  setupTaskCount: z.int().min(0),
  ceremonyTaskCount: z.int().min(0),
  dependencyCount: z.int().min(0),
  iterationCount: z.int().min(0),
  /** Elements the source method holds that produced no task, with why in `warnings`. */
  plannedElementCount: z.int().min(0),
  skippedElementCount: z.int().min(0),
  /** Longest finish-to-start chain, in working days: the span the plan cannot beat. */
  criticalPathDays: DurationDaysSchema,
  criticalPathTaskIds: z.array(IdentifierSchema).max(500),
  warnings: z.array(PlanWarningSchema).max(500),
});
export type PlanGenerationReport = z.infer<typeof PlanGenerationReportSchema>;

/** Identity, provenance and shape — everything but the breakdown itself. */
export const PlanSummarySchema = z.strictObject({
  id: IdentifierSchema,
  name: LabelSchema,
  description: DescriptionSchema.nullable(),
  /** The organisation this plan is for. A base-method plan may have none. */
  clientName: ShortTextSchema.nullable(),
  state: PlanStateSchema,
  templateKey: PlanTemplateKeySchema,
  sourceMethodVersionId: IdentifierSchema,
  /** The adoption session the source method came out of, when it came out of one. */
  workshopSessionId: IdentifierSchema.nullable(),
  startsOn: IsoDateSchema.nullable(),
  workingDaysPerWeek: z.int().min(1).max(7),
  revision: RevisionSchema,
  taskCount: z.int().min(0),
  milestoneCount: z.int().min(0),
  /** Working days from the first task's start to the last one's finish. */
  spanDays: DurationDaysSchema,
  generatedAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  createdBy: ShortTextSchema,
  updatedBy: ShortTextSchema,
  baselinedAt: IsoDateTimeSchema.nullable(),
});
export type PlanSummary = z.infer<typeof PlanSummarySchema>;

/**
 * A list row. Carries the source method's name because a plan list that showed only
 * `sourceMethodVersionId` would be a list of uuids, and the join is one the planning
 * context already has to make to render its own breadcrumb.
 */
export const PlanListItemSchema = z.strictObject({
  ...PlanSummarySchema.shape,
  sourceMethodName: LabelSchema,
  sourceMethodVersionLabel: ShortTextSchema,
  templateName: LabelSchema,
  templateFamily: PlanTemplateFamilySchema,
});
export type PlanListItem = z.infer<typeof PlanListItemSchema>;

export const PlanDetailSchema = z.strictObject({
  ...PlanSummarySchema.shape,
  tasks: z.array(PlanTaskSchema),
  dependencies: z.array(PlanTaskDependencySchema),
  parameterValues: z.array(PlanParameterValueSchema),
  report: PlanGenerationReportSchema,
});
export type PlanDetail = z.infer<typeof PlanDetailSchema>;

/* ── reads ──────────────────────────────────────────────────────────────── */

export const ListPlansRequestSchema = z.strictObject({
  ...PageQuerySchema.shape,
  q: TextFilterSchema.shape.q,
  ...sortBy(PLAN_SORT_FIELDS).shape,
  state: PlanStateSchema.optional(),
  templateKey: PlanTemplateKeySchema.optional(),
  sourceMethodVersionId: IdentifierSchema.optional(),
  workshopSessionId: IdentifierSchema.optional(),
});
export type ListPlansRequest = z.infer<typeof ListPlansRequestSchema>;
export const ListPlansResponseSchema = paginated(PlanListItemSchema);
export type ListPlansResponse = z.infer<typeof ListPlansResponseSchema>;

export const GetPlanRequestSchema = z.strictObject({ planId: IdentifierSchema });
export type GetPlanRequest = z.infer<typeof GetPlanRequestSchema>;
export const GetPlanResponseSchema = z.strictObject({
  plan: PlanDetailSchema,
  sourceMethod: MethodVersionSummarySchema,
  /** The rules the plan was generated under, so the two can be read side by side. */
  template: PlanTemplateSchema,
});
export type GetPlanResponse = z.infer<typeof GetPlanResponseSchema>;

/* ── generation ─────────────────────────────────────────────────────────── */

/** One answer to one template or method parameter. */
export const PlanParameterAnswerSchema = z.strictObject({
  key: ParameterKeySchema,
  value: ShortTextSchema,
});
export type PlanParameterAnswer = z.infer<typeof PlanParameterAnswerSchema>;

/**
 * What to leave out.
 *
 * Every default is "include", because the pre-launch work is the part that gets forgotten
 * and a generator whose defaults were minimal would be complicit in forgetting it. The
 * switches exist for the second plan: an organisation on its third adoption of the same
 * method does not need the training section again.
 */
export const PlanGenerationOptionsSchema = z.strictObject({
  includeTraining: z.boolean().optional(),
  includeToolSetup: z.boolean().optional(),
  includeAssetConfiguration: z.boolean().optional(),
  includeAgentEnablement: z.boolean().optional(),
  includeCeremonies: z.boolean().optional(),
  includeDeliverableTasks: z.boolean().optional(),
  /**
   * There is deliberately no `includeSkippedElements`. A skipped element is not filtered late,
   * it is absent: what the generator reads is the published graph of the tailored version, and
   * a client's SKIP decision means the element is not in it. Honouring such a flag would mean
   * generating from the *base* method's graph and re-adding what the client declined, which is
   * the one thing an adoption plan must never do.
   */
});
export type PlanGenerationOptions = z.infer<typeof PlanGenerationOptionsSchema>;

/**
 * Generate a plan.
 *
 * `sourceMethodVersionId` is always required and `workshopSessionId` never substitutes for
 * it: a tailored method *is* a method version, so there is one code path for "base method"
 * and "adopted method" and no branch anywhere asking which kind of plan this is. The
 * session, when named, adds what the graph does not carry — the client's binding
 * configuration text and their parameter answers — and is validated to be a session that
 * published this very version, so a plan can never quote one client's configuration into
 * another client's method.
 */
export const GeneratePlanRequestSchema = z.strictObject({
  name: LabelSchema,
  description: DescriptionSchema.optional(),
  clientName: ShortTextSchema.optional(),
  templateKey: PlanTemplateKeySchema,
  sourceMethodVersionId: IdentifierSchema,
  workshopSessionId: IdentifierSchema.optional(),
  startsOn: IsoDateSchema.optional(),
  workingDaysPerWeek: z.int().min(1).max(7).optional(),
  parameterValues: z.array(PlanParameterAnswerSchema).max(64).optional(),
  options: PlanGenerationOptionsSchema.optional(),
});
export type GeneratePlanRequest = z.infer<typeof GeneratePlanRequestSchema>;
export const GeneratePlanResponseSchema = z.strictObject({ plan: PlanDetailSchema });
export type GeneratePlanResponse = z.infer<typeof GeneratePlanResponseSchema>;

/**
 * Replay the generator over the same plan.
 *
 * `keepCurated` defaults to true and is the whole reason this route exists rather than
 * "delete it and generate again": the method moved on, and the four durations a planner
 * argued about with the client did not.
 */
export const RegeneratePlanRequestSchema = z.strictObject({
  planId: IdentifierSchema,
  expectedRevision: RevisionSchema,
  /** Switch template. Absent keeps the one the plan was generated with. */
  templateKey: PlanTemplateKeySchema.optional(),
  parameterValues: z.array(PlanParameterAnswerSchema).max(64).optional(),
  options: PlanGenerationOptionsSchema.optional(),
  startsOn: IsoDateSchema.nullable().optional(),
  keepCurated: z.boolean().optional(),
});
export type RegeneratePlanRequest = z.infer<typeof RegeneratePlanRequestSchema>;
export const RegeneratePlanResponseSchema = z.strictObject({
  plan: PlanDetailSchema,
  /** Generated tasks discarded and rebuilt. */
  replacedTaskCount: z.int().min(0),
  /** Manual and curated tasks carried across untouched. */
  keptTaskCount: z.int().min(0),
  /** Curated tasks whose method element has gone: kept, and orphaned. */
  orphanedTaskIds: z.array(IdentifierSchema).max(500),
});
export type RegeneratePlanResponse = z.infer<typeof RegeneratePlanResponseSchema>;

/* ── curation ───────────────────────────────────────────────────────────── */

/**
 * Rename the plan, move its start, change the working week.
 *
 * `startsOn` and `workingDaysPerWeek` change no offset and no duration — they change the
 * calendar the same offsets are read against, which is exactly why the schedule is stored
 * as offsets. Moving a plan's start is a one-field write rather than a regeneration.
 */
export const UpdatePlanRequestSchema = z.strictObject({
  planId: IdentifierSchema,
  expectedRevision: RevisionSchema,
  name: LabelSchema.optional(),
  description: DescriptionSchema.nullable().optional(),
  clientName: ShortTextSchema.nullable().optional(),
  startsOn: IsoDateSchema.nullable().optional(),
  workingDaysPerWeek: z.int().min(1).max(7).optional(),
});
export type UpdatePlanRequest = z.infer<typeof UpdatePlanRequestSchema>;
export const UpdatePlanResponseSchema = z.strictObject({ plan: PlanSummarySchema });
export type UpdatePlanResponse = z.infer<typeof UpdatePlanResponseSchema>;

/**
 * Freeze the plan as agreed.
 *
 * One-way on purpose: a baselined plan is what the sponsor signed, so the only way past it
 * is a new plan. `ARCHIVED` is reached the same way — nothing un-archives, because a plan
 * nobody is working to is not a plan that needs editing.
 */
export const BaselinePlanRequestSchema = z.strictObject({
  planId: IdentifierSchema,
  expectedRevision: RevisionSchema,
  note: DescriptionSchema.optional(),
});
export type BaselinePlanRequest = z.infer<typeof BaselinePlanRequestSchema>;
export const BaselinePlanResponseSchema = z.strictObject({
  plan: PlanSummarySchema,
  baselinedAt: IsoDateTimeSchema,
  /** Warnings still standing at baseline. Frozen with a hole in it is worth saying out loud. */
  outstandingWarnings: z.array(PlanWarningSchema).max(500),
});
export type BaselinePlanResponse = z.infer<typeof BaselinePlanResponseSchema>;

export const ArchivePlanRequestSchema = z.strictObject({
  planId: IdentifierSchema,
  expectedRevision: RevisionSchema,
});
export type ArchivePlanRequest = z.infer<typeof ArchivePlanRequestSchema>;
export const ArchivePlanResponseSchema = z.strictObject({ plan: PlanSummarySchema });
export type ArchivePlanResponse = z.infer<typeof ArchivePlanResponseSchema>;

export const DeletePlanRequestSchema = z.strictObject({
  planId: IdentifierSchema,
  expectedRevision: QueryIntSchema,
});
export type DeletePlanRequest = z.infer<typeof DeletePlanRequestSchema>;
export const DeletePlanResponseSchema = z.strictObject({
  planId: IdentifierSchema,
  deletedTaskCount: z.int().min(0),
});
export type DeletePlanResponse = z.infer<typeof DeletePlanResponseSchema>;

/**
 * Add a task the method did not ask for.
 *
 * Always `MANUAL` origin — the caller does not get to claim a task came from the method —
 * and always `curated`, so the next regeneration keeps it.
 */
export const AddPlanTaskRequestSchema = z.strictObject({
  planId: IdentifierSchema,
  expectedRevision: RevisionSchema,
  parentTaskId: IdentifierSchema.nullable().optional(),
  /** Place it after this sibling. Absent appends. */
  afterTaskId: IdentifierSchema.nullable().optional(),
  name: LabelSchema,
  description: DescriptionSchema.optional(),
  kind: PlanTaskKindSchema,
  section: PlanSectionSchema,
  durationDays: DurationDaysSchema.optional(),
  ownerRoleId: RoleIdSchema.optional(),
  ownerName: ShortTextSchema.optional(),
  iteration: z.int().min(1).optional(),
});
export type AddPlanTaskRequest = z.infer<typeof AddPlanTaskRequestSchema>;

/**
 * Every write to a plan answers with the whole plan.
 *
 * Not a preference: adding a task renumbers its siblings' `wbsCode`, and changing one
 * duration moves every `earliestStartDay` downstream of it. A response carrying only the
 * edited row would leave the client holding a schedule that is quietly wrong, and a client
 * that refetched afterwards would be doing the same round trip with an extra flicker.
 */
export const PlanCommandResponseSchema = z.strictObject({
  plan: PlanDetailSchema,
  /** The task the command was about, or `null` for a delete. */
  task: PlanTaskSchema.nullable(),
});
export type PlanCommandResponse = z.infer<typeof PlanCommandResponseSchema>;

/**
 * Edit one task.
 *
 * Absent leaves alone, `null` clears — the same rule the authoring PATCH commands use, and
 * for the same reason: without it, an owner assigned by mistake could never be unassigned.
 * Any field arriving here marks the task `curated`.
 */
export const UpdatePlanTaskRequestSchema = z.strictObject({
  planId: IdentifierSchema,
  taskId: IdentifierSchema,
  expectedRevision: RevisionSchema,
  name: LabelSchema.optional(),
  description: DescriptionSchema.nullable().optional(),
  kind: PlanTaskKindSchema.optional(),
  section: PlanSectionSchema.optional(),
  durationDays: DurationDaysSchema.optional(),
  ownerRoleId: RoleIdSchema.nullable().optional(),
  ownerName: ShortTextSchema.nullable().optional(),
  configuration: DescriptionSchema.nullable().optional(),
  iteration: z.int().min(1).nullable().optional(),
  recurrence: PlanRecurrenceSchema.nullable().optional(),
  /** Re-parent. `null` moves the task to the top level. */
  parentTaskId: IdentifierSchema.nullable().optional(),
  /** Position among siblings. */
  order: OrderSchema.optional(),
});
export type UpdatePlanTaskRequest = z.infer<typeof UpdatePlanTaskRequestSchema>;

export const DeletePlanTaskRequestSchema = z.strictObject({
  planId: IdentifierSchema,
  taskId: IdentifierSchema,
  expectedRevision: QueryIntSchema,
});
export type DeletePlanTaskRequest = z.infer<typeof DeletePlanTaskRequestSchema>;
export const DeletePlanTaskResponseSchema = z.strictObject({
  plan: PlanDetailSchema,
  /** The task and everything beneath it: deleting a summary deletes its children. */
  deletedTaskIds: z.array(IdentifierSchema).max(500),
  /** Dependencies dropped because an end of them no longer exists. */
  droppedDependencyCount: z.int().min(0),
});
export type DeletePlanTaskResponse = z.infer<typeof DeletePlanTaskResponseSchema>;

/**
 * Declare or replace one dependency.
 *
 * `PUT` and not `POST` because the pair `(fromTaskId, toTaskId)` identifies it: asking for
 * the same edge twice is one edge, and a planner dragging the same arrow twice has not
 * created a second one. A cycle is refused with `VALIDATION_FAILED` — an unschedulable plan
 * is not a plan, and the alternative (accept it and report it) means every reader of the
 * schedule has to handle a graph that has no topological order.
 */
export const SetPlanDependencyRequestSchema = z.strictObject({
  planId: IdentifierSchema,
  expectedRevision: RevisionSchema,
  fromTaskId: IdentifierSchema,
  toTaskId: IdentifierSchema,
  kind: PlanDependencyKindSchema,
  lagDays: z.int().min(-365).max(365).optional(),
});
export type SetPlanDependencyRequest = z.infer<typeof SetPlanDependencyRequestSchema>;
export const SetPlanDependencyResponseSchema = z.strictObject({
  plan: PlanDetailSchema,
  dependency: PlanTaskDependencySchema,
});
export type SetPlanDependencyResponse = z.infer<typeof SetPlanDependencyResponseSchema>;

export const DeletePlanDependencyRequestSchema = z.strictObject({
  planId: IdentifierSchema,
  dependencyId: IdentifierSchema,
  expectedRevision: QueryIntSchema,
});
export type DeletePlanDependencyRequest = z.infer<typeof DeletePlanDependencyRequestSchema>;
export const DeletePlanDependencyResponseSchema = z.strictObject({
  plan: PlanDetailSchema,
  dependencyId: IdentifierSchema,
});
export type DeletePlanDependencyResponse = z.infer<typeof DeletePlanDependencyResponseSchema>;
